import { TIMEZONE_DATA } from './constants';

/**
 * Parses a timezone string like "GMT+05:30" or "GMT-05:00" to offset in minutes.
 * Returns negative minutes for West of GMT (e.g., -300 for GMT-05:00)
 * Returns positive minutes for East of GMT (e.g., 330 for GMT+05:30)
 */
export const parseTimezoneOffset = (tzString) => {
    if (!tzString) return 0;

    // Extract sign, hours, minutes
    const match = tzString.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!match) return 0;

    const [, sign, hours, minutes] = match;
    const offsetMinutes = (parseInt(hours, 10) * 60) + parseInt(minutes, 10);

    return sign === '+' ? offsetMinutes : -offsetMinutes;
};

/**
 * Formats a date string adjusting for the given user timezone preference.
 * 
 * @param {string} dateString - ISO date string (e.g., "2026-02-14T14:38:00Z" or "2026-02-14T14:38:00")
 * @param {string} userTimezone - Timezone preference string (e.g., "GMT+05:30")
 * @param {Object} options - Intl.DateTimeFormat options (e.g., { month: 'short', ... })
 * @returns {string} Formatted date string
 */
export const formatDateWithTimezone = (dateString, userTimezone, options = {}) => {
    if (!dateString) return '';

    // 1. Force UTC interpretation if no timezone info is present
    // Supabase returns dates like "2026-02-22T14:58:57.147111+00:00" (with offset)
    // Only append 'Z' if the string has NO timezone indicator (no Z, no +/-offset)
    let normalized = dateString;
    const hasTimezone = /Z$/.test(dateString) || /[+-]\d{2}:\d{2}$/.test(dateString);
    if (!hasTimezone) {
        normalized = `${dateString}Z`;
    }
    const utcDate = new Date(normalized);

    if (isNaN(utcDate.getTime())) return '';

    // 2. Calculate target offset in minutes
    // Default to browser's offset if no preference is provided
    let targetOffsetMinutes;
    if (userTimezone) {
        targetOffsetMinutes = parseTimezoneOffset(userTimezone);
    } else {
        targetOffsetMinutes = -new Date().getTimezoneOffset(); // Browser offset (note sign inversion)
    }

    // 3. Create a "shifted" date object that represents the target local time in UTC numbers
    // Example: 09:00 UTC. Target GMT+5:30.
    // We want to force the Date object to hold values 14:30.
    // 09:00 UTC is timestamp X. 
    // Shifted timestamp = X + (targetOffsetMinutes * 60000).
    const shiftedTimestamp = utcDate.getTime() + (targetOffsetMinutes * 60000);
    const shiftedDate = new Date(shiftedTimestamp);

    // 4. Use getUTC* methods to extract the "local" components from the shifted date
    // We construct a new Date from these components to format it using toLocaleString but assuming UTC
    // Or we can manually format it?
    // Better: use toLocaleString('en-US', { timeZone: 'UTC', ... }) on the shifted date.
    // This treats the shifted timestamp as if it were the "true" time in UTC, which matches the visual time we want.

    return shiftedDate.toLocaleString('en-US', {
        timeZone: 'UTC',
        ...options
    });
};

/**
 * Returns a relative time string (e.g., "5m ago")
 * Timezone independent as it compares UTC timestamps.
 */
export const timeAgo = (dateString) => {
    if (!dateString) return 'N/A';

    const hasTimezone = /Z$/.test(dateString) || /[+-]\d{2}:\d{2}$/.test(dateString);
    const normalized = hasTimezone ? dateString : `${dateString}Z`;
    const date = new Date(normalized);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Detects the user's location and preferred timezone based on browser settings.
 * Returns { country, timezone } matching values in TIMEZONE_DATA.
 */
export const detectUserLocation = () => {
    try {
        // Get browser offset in minutes (inverted sign: e.g. -330 for India)
        const offsetMinutes = -new Date().getTimezoneOffset();

        // Convert to GMT string format (e.g., GMT+05:30)
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const absOffset = Math.abs(offsetMinutes);
        const hours = Math.floor(absOffset / 60).toString().padStart(2, '0');
        const minutes = (absOffset % 60).toString().padStart(2, '0');
        const gmtString = `GMT${sign}${hours}:${minutes}`; // e.g., "GMT+05:30"

        // Search TIMEZONE_DATA for this GMT string
        for (const [country, timezones] of Object.entries(TIMEZONE_DATA)) {
            const match = timezones.find(tz => tz.value === gmtString);
            if (match) {
                return { country, timezone: match.value };
            }
        }

        // Fallback: Default to US / GMT-05:00 if no match found
        return { country: 'United States', timezone: 'GMT-05:00' };
    } catch (e) {
        console.error('Error detecting location:', e);
        return { country: 'United States', timezone: 'GMT-05:00' };
    }
};

/**
 * Formats a duration in seconds to a short string (e.g., "5s", "2m", "1h").
 */
export const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};
